import React, { Fragment } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import { SITENAMEALIAS } from "../utils/init";
import { Link } from "react-router-dom";
import { showToast, showHttpError, currentPageAccess } from "../utils/library";
import {
  FetchMerchantStatus,
  ResendEmailConfirm,
  GetUserDetails,
  getAuthToken,
  getAuthUuid,
  getMerchantCardApplications,
} from "../utils/service";
import { getUserKycDetails } from "../utils/kycService";

/* ─── Keyframes injected once ─── */
const STYLE_ID = "gs-keyframes";
if (!document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes gs-pulse-step {
      0%,100% { box-shadow:0 0 0 0 rgba(59,110,246,0.45); }
      50%      { box-shadow:0 0 0 8px rgba(59,110,246,0); }
    }
    @keyframes gs-fresh {
      from { opacity:0.3; } to { opacity:1; }
    }
    @keyframes gs-banner-in {
      from { opacity:0; transform:translateY(-8px); }
      to   { opacity:1; transform:translateY(0); }
    }
  `;
  document.head.appendChild(s);
}

/* ─── CRITICAL: robust completion check ─────────────────────────────────
   localStorage always returns strings, but after setState the value is
   whatever the API returned (could be "1", 1, or true).
   This helper normalises all forms to a boolean. */
const isDone = (val) => val === "1" || val === 1 || val === true;

// On https://trade.paybito.com we require localStorage credentials
// (access_token, brokerId, uuid). If any are missing, GetStartedNew bails
// out to the login page silently to avoid a blank screen / API loop.
const isTradePaybitoMissingCreds = () => {
  if (window.location.origin !== "https://trade.paybito.com") return false;
  return (
    !getAuthToken() ||
    !localStorage.getItem(SITENAMEALIAS + "_brokerId") ||
    !getAuthUuid()
  );
};

class GetStartedNew extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showLoader: false,
      /* Read from localStorage so first render is already correct */
      isEmailConfirmed: localStorage.getItem(
        SITENAMEALIAS + "_email_confirmed"
      ),
      isVerificationSubmitted: localStorage.getItem(
        SITENAMEALIAS + "_basic_verification_submitted"
      ),
      isCryptoAddressAdded: localStorage.getItem(
        SITENAMEALIAS + "_crypto_address_added"
      ),
      isEmailResend: false,
      permission: "",
      bannerDismissed: sessionStorage.getItem("gs_banner_dismissed") === "1",
      shouldSkipRender: isTradePaybitoMissingCreds(),
      kycStatus: -1, // Default: Not started
      cardApplications: [],
    };
  }

  /* ─── API: refresh merchant status ─── */
  handleMerchantStatus = () => {
    this.setState({ showLoader: true });
    // Sub-merchants should see their parent merchant's verification details,
    // since onboarding/verification status lives on the parent account.
    const parentMerchantId = localStorage.getItem(
      SITENAMEALIAS + "_parent_merchant_id"
    );
    const isSubMerchant = parentMerchantId && parentMerchantId != "0";
    const payload = {
      merchant_id: isSubMerchant
        ? parentMerchantId
        : localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
    };
    FetchMerchantStatus(payload)
      .then((res) => {
        this.setState({ showLoader: false });
        const response = res.data[0];
        if (response.error !== "0") {
          showToast("error", response.error_msg);
        } else {
          localStorage.setItem(
            SITENAMEALIAS + "_email_confirmed",
            response["email_confirmed"]
          );
          localStorage.setItem(
            SITENAMEALIAS + "_basic_verification_submitted",
            response["basic_verification_submitted"]
          );
          localStorage.setItem(
            SITENAMEALIAS + "_crypto_address_added",
            response["crypto_address_added"]
          );
          localStorage.setItem(
            SITENAMEALIAS + "_account_status_id",
            response["account_status_id"]
          );
          /* Store raw API values; isDone() normalises on read */
          this.setState({
            isEmailConfirmed: response["email_confirmed"],
            isVerificationSubmitted: response["basic_verification_submitted"],
            isCryptoAddressAdded: response["crypto_address_added"],
          });
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });

    // Fetch KYC status details exactly like in KycContent.jsx
    const uuid = getAuthUuid();
    if (uuid) {
      // 1. Primary KYC details
      getUserKycDetails({ uuid: uuid })
        .then((res) => {
          let r = res.data;
          let d;
          if (r && r.error && r.error.error_data === 0 && r.userResult) { d = r.userResult; }
          else if (r && r.uuid) { d = r; }

          if (d) {
            let kycStatus = -1;
            // Robust check for completion status
            if (isDone(d.isKycFinished)) {
              const status = String(d.userDocsStatus);
              if (status === "0") kycStatus = 0;
              else if (status === "1") kycStatus = 1;
              else if (status === "2") kycStatus = 2;
            }
            this.setState({ kycStatus: kycStatus });
          }
        })
        .catch(() => { });

      // 2. Secondary user details (to ensure parity with KycContent logic)
      GetUserDetails()
        .then((res) => {
          const data = res.data;
          if (data && data.userDocsStatus !== undefined) {
            const status = String(data.userDocsStatus);
            let kycStatus = -1;
            if (status === "0") kycStatus = 0;
            else if (status === "1") kycStatus = 1;
            else if (status === "2") kycStatus = 2;

            // If this response has more specific status, use it
            if (kycStatus !== -1) {
              this.setState({ kycStatus: kycStatus });
            }
          }
        })
        .catch(() => { });
    }
  };

  /* ─── API: resend confirmation email ─── */
  handleResendEmail = () => {
    this.setState({ showLoader: true });
    const payload = {
      merchant_id: localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
    };
    ResendEmailConfirm(payload)
      .then((res) => {
        this.setState({ showLoader: false });
        const response = res.data[0];
        if (response.error !== "0") {
          showToast("error", response.error_msg);
        } else {
          this.setState({ isEmailResend: true });
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  /* ─── Step helpers ─── */
  getCompletedSteps = () => ({
    step1: isDone(this.state.isEmailConfirmed),
    step2: isDone(this.state.isVerificationSubmitted),
    step3: isDone(this.state.isCryptoAddressAdded),
  });

  getActiveStep = () => {
    if (!isDone(this.state.isEmailConfirmed)) return 1;
    if (!isDone(this.state.isVerificationSubmitted)) return 2;
    if (!isDone(this.state.isCryptoAddressAdded)) return 3;
    return 0; /* all done */
  };

  dismissBanner = () => {
    sessionStorage.setItem("gs_banner_dismissed", "1");
    this.setState({ bannerDismissed: true });
  };

  /* ─── Timer (unchanged from original) ─── */
  startTimerForEmailOtp = (i) => {
    let count = i;
    this.setState({ timerTxtEmail: count }, () => {
      const timer = setInterval(() => {
        if (count <= 1) {
          clearInterval(timer);
          this.setState({ secondStatusCheckForEmailOtp: false });
          localStorage.setItem("secondStatusCheckForEmailOtp", "false");
          this.state.isTimerStartEmailGetCode = false;
        }
        count -= 1;
        localStorage.setItem("cdforemail", count);
        this.setState({ timerTxtEmail: localStorage.getItem("cdforemail") });
      }, 1000);
    });
  };

  componentDidMount() {
    // On trade.paybito.com without localStorage creds, silently redirect
    // to the login route ("/" — Login is mounted there). Skip all API
    // calls and timer setup so nothing flashes and no loop kicks in.
    if (this.state.shouldSkipRender) {
      // Defensive: clear partial creds so Login does not bounce back to /autologin
      localStorage.removeItem(SITENAMEALIAS + "_access_token");
      localStorage.removeItem(SITENAMEALIAS + "_brokerId");
      localStorage.removeItem(SITENAMEALIAS + "_uuid");
      if (this.props.history) {
        this.props.history.replace("/");
      } else {
        window.location.replace("/");
      }
      return;
    }

    this.setState({ permission: currentPageAccess() });
    if (localStorage.getItem("secondStatusCheckForEmailOtp") === "true") {
      this.startTimerForEmailOtp(localStorage.getItem("cdforemail"));
      this.setState({
        secondStatusCheckForEmailOtp: true,
        isTimerStartEmailGetCode: true,
        timerTxtEmail: localStorage.getItem("cdforemail"),
        loginBtn: false,
      });
    } else {
      this.setState({ secondStatusCheckForEmailOtp: false });
    }
    this.handleMerchantStatus();
    this.fetchCardApplications();
  }

  fetchCardApplications = () => {
    const parentMerchantId = localStorage.getItem(SITENAMEALIAS + "_parent_merchant_id");
    const isSubMerchant = parentMerchantId && parentMerchantId !== "0";
    let merchantId = isSubMerchant
      ? parentMerchantId
      : localStorage.getItem(SITENAMEALIAS + "_merchant_id");

    if (!merchantId) return;

    getMerchantCardApplications(merchantId, getAuthUuid() || "")
      .then((res) => {
        if (res.data && res.data.error === "0") {
          this.setState({ cardApplications: res.data.card_applications || [] });
        }
      })
      .catch((err) => {
        console.error("Error fetching card applications:", err);
      });
  };

  /* ══════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════ */
  render() {
    // Skip render entirely while we redirect off trade.paybito.com.
    if (this.state.shouldSkipRender) {
      return null;
    }

    const steps = this.getCompletedSteps();
    const activeStep = this.getActiveStep();
    const kycVerified = this.state.kycStatus === 1;
    const allComplete = activeStep === 0 && kycVerified;
    const showBanner = steps.step2 && !this.state.bannerDismissed;

    const stepLabels = [
      "Confirm Email",
      "Business Activation",
      "Accept Payment",
    ];
    const stepDone = [steps.step1, steps.step2, steps.step3];

    const pageTitle = allComplete
      ? "Account fully set up 🎉"
      : activeStep === 1
        ? "Confirm your email"
        : activeStep === 2
          ? "Activate your business"
          : "Start accepting payments";

    const pageSubtitle = allComplete
      ? "You're all set to start accepting payments."
      : activeStep === 3
        ? "Create your first payment link in under a minute."
        : "Complete the steps below to unlock payments.";

    const kycBtnLabel =
      this.state.kycStatus === 0
        ? "In review"
        : this.state.kycStatus === 1
          ? "Accepted"
          : this.state.kycStatus === 2
            ? "Resubmit"
            : "Apply Now";

    return (
      <Fragment>
        <style>{`
          @media (max-width: 576px) {
            .gs-step-label {
              white-space: normal !important;
              text-align: center !important;
              max-width: 68px !important;
              font-size: 10px !important;
            }
            .gs-step-progress {
              gap: 2px !important;
              max-width: 100% !important;
              padding: 0 8px !important;
            }
          }
        `}</style>
        <div className="container-scroller">
          <Sidebar />
          <Header />

          <div className="main-panel">
            <div style={sx.mainInner}>
              {/* ── Arrival banner ── */}
              {showBanner && (
                <div style={sx.arrivalBanner} data-tour="success-banner">
                  <span style={{ flex: 1 }}>
                    🎉 Business verification successful! You're all set to start
                    accepting payments.
                  </span>
                  <button style={sx.arrivalClose} onClick={this.dismissBanner}>
                    ✕
                  </button>
                </div>
              )}

              {/* ── Step Progress ── */}
              <div
                className="gs-step-progress"
                style={sx.stepProgress}
                data-tour="progress-tracker"
              >
                {stepLabels.map((label, idx) => {
                  const done = stepDone[idx];
                  const isActive = activeStep === idx + 1;
                  return (
                    <React.Fragment key={idx}>
                      <div style={sx.stepNode}>
                        <div style={stepCircleStyle(done, isActive)}>
                          {done ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            /* Inline span so global * CSS doesn't override the circle's color */
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: 700,
                                color: isActive
                                  ? "#3b6ef6"
                                  : "var(--text-color)",
                                opacity: isActive ? 1 : 0.45,
                              }}
                            >
                              {idx + 1}
                            </span>
                          )}
                        </div>
                        <span
                          className="gs-step-label"
                          style={stepLabelStyle(done, isActive)}
                        >
                          {label}
                        </span>
                      </div>
                      {idx < stepLabels.length - 1 && (
                        <div style={connectorStyle(done)} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* ── Page header ── */}
              <div style={sx.pageHeader} data-tour="main-header">
                <h1 style={sx.pageTitle}>{pageTitle}</h1>
                <p style={sx.pageSubtitle}>{pageSubtitle}</p>
              </div>

              {/* ── Action buttons: only visible at step 3 or all done ── */}
              {(activeStep === 3 || allComplete) &&
                this.state.permission === "WRITE" && (
                  <div style={sx.actionButtons}>
                    <Link
                      to="/create-payment"
                      style={sx.btnPrimary}
                      data-tour="create-payment-btn"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2.5"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Create Payment
                    </Link>
                    <button
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent("openQuickPayment")
                        )
                      }
                      style={sx.btnOutline}
                      data-tour="quick-payment-btn"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                      </svg>
                      Quick Payment
                    </button>
                  </div>
                )}

              {/* ══ Setup Checklist Card ══ */}
              <div style={sx.checklistCard}>
                <div style={sx.cardLabel}>Setup</div>
                <div style={sx.cardLabelSub}>
                  Track your onboarding progress
                </div>

                {/* Row 1 — Email */}
                <div style={sx.checklistRow}>
                  <div
                    style={steps.step1 ? sx.checkIconGreen : sx.checkIconBlue}
                  >
                    {steps.step1 ? (
                      <CheckSVG color="#10b981" />
                    ) : (
                      <InfoSVG color="var(--btn-color)" />
                    )}
                  </div>
                  <div style={sx.checkLabelWrap}>
                    <div style={sx.checkLabel}>Email verified</div>
                    {!steps.step1 && (
                      <div style={sx.checkStatusBlue}>Pending</div>
                    )}
                  </div>
                  {!steps.step1 &&
                    this.state.permission === "WRITE" &&
                    (!this.state.isEmailResend ? (
                      <button
                        style={sx.btnMini}
                        onClick={this.handleResendEmail}
                      >
                        Resend
                      </button>
                    ) : (
                      <span style={sx.sentLabel}>Sent ✓</span>
                    ))}
                  {steps.step1 && (
                    <span style={sx.checkStatusGreen}>Done ✓</span>
                  )}
                </div>

                {/* Row 2 — Business Activation */}
                <div
                  style={{
                    ...sx.checklistRow,
                    ...(steps.step2 ? sx.rowFresh : {}),
                  }}
                >
                  <div
                    style={steps.step2 ? sx.checkIconGreen : sx.checkIconBlue}
                  >
                    {steps.step2 ? (
                      <CheckSVG color="#10b981" />
                    ) : (
                      <UserSVG color="var(--btn-color)" />
                    )}
                  </div>
                  <div style={sx.checkLabelWrap}>
                    <div style={sx.checkLabel}>
                      {steps.step2 ? (
                        <span style={{ color: "#10b981" }}>
                          Business activated, you are now ready to accept crypto payments
                        </span>
                      ) : (
                        "Business activated"
                      )}
                    </div>
                    {!steps.step2 && (
                      <div style={sx.checkStatusBlue}>Action required</div>
                    )}
                  </div>
                  {steps.step2 ? (
                    <>
                      {this.state.permission === "WRITE" && (
                        <Link to="/basic-verification" style={sx.btnMiniLink}>
                          View
                        </Link>
                      )}
                      <span style={sx.checkStatusGreen}>Done ✓</span>
                    </>
                  ) : this.state.permission === "WRITE" ? (
                    <Link to="/basic-verification" style={sx.btnMiniLink}>
                      Activate
                    </Link>
                  ) : (
                    <button
                      style={{
                        ...sx.btnMini,
                        opacity: 0.4,
                        cursor: "not-allowed",
                      }}
                      disabled
                      onClick={() =>
                        showToast(
                          "error",
                          "You need write access to perform this operation"
                        )
                      }
                    >
                      Activate
                    </button>
                  )}
                </div>

                {/* Row 3 — Create Payment Link */}
                <div
                  style={{
                    ...sx.checklistRow,
                    borderBottom: "none",
                    paddingBottom: 0,
                  }}
                  data-tour="setup-payment-link"
                >
                  <div
                    style={steps.step3 ? sx.checkIconGreen : sx.checkIconBlue}
                  >
                    {steps.step3 ? (
                      <CheckSVG color="#10b981" />
                    ) : (
                      <BoltSVG color="var(--btn-color)" />
                    )}
                  </div>
                  <div style={sx.checkLabelWrap}>
                    <div style={{ ...sx.checkLabel, fontWeight: 600 }}>
                      {steps.step3
                        ? "Payment link created"
                        : "Create your first payment link"}
                    </div>
                    {!steps.step3 && (
                      <div style={sx.checkStatusBlue}>Action required</div>
                    )}
                  </div>
                  {steps.step3 ? (
                    <span style={sx.checkStatusGreen}>Done ✓</span>
                  ) : steps.step2 && this.state.permission === "WRITE" ? (
                    <Link to="/create-payment" style={sx.btnMiniLink}>
                      Create
                    </Link>
                  ) : (
                    <button
                      style={{
                        ...sx.btnMini,
                        opacity: 0.4,
                        cursor: "not-allowed",
                      }}
                      disabled
                      onClick={() =>
                        !steps.step2
                          ? showToast(
                            "error",
                            "Please complete Business Activation (Step 2) first"
                          )
                          : showToast(
                            "error",
                            "You need write access to perform this operation"
                          )
                      }
                    >
                      Create
                    </button>
                  )}
                </div>
              </div>

              {/* ── Card Payments Promo (only when business activation is done) ── */}
              {steps.step2 && (
                <div style={sx.promoCard}>
                  <div style={sx.promoContent}>
                    <div style={sx.promoIcon}>💳</div>
                    <div style={{ flex: 1 }}>
                      <h3 style={sx.promoTitle}>Start Accepting Card Payments</h3>
                      <p style={sx.promoText}>
                        Enable secure credit and debit card processing for your
                        business with a quick application.
                      </p>
                      <ul style={sx.promoList}>
                        <li>✓ Accept major card networks</li>
                        <li>✓ Fast onboarding process</li>
                        <li>✓ Secure & compliant payments</li>
                        <li style={{ opacity: 0.7, marginTop: "4px" }}>
                          Application review typically takes 5-7 business days.
                        </li>
                      </ul>
                    </div>
                    <div style={sx.promoAction}>
                      <Link
                        to="/identity-verification"
                        style={this.state.kycStatus === 1 ? sx.btnSuccess : sx.btnPrimary}
                      >
                        {kycBtnLabel}
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Card Applications Section */}
              {this.state.cardApplications && this.state.cardApplications.length > 0 && (
                <div style={{ marginTop: "24px", background: "var(--body-color-less)", padding: "24px", borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid var(--body-color-less2)" }}>
                  <h3 style={{ fontSize: "18px", fontWeight: "700", marginBottom: "16px", color: "var(--text-color)" }}>Card Applications</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {this.state.cardApplications.map((app, index) => {
                      const normalized = app.cardProcessorName ? app.cardProcessorName.replace(/\s+/g, '').toLowerCase() : '';
                      const map = {
                        stripe: "PayBito Apex",
                        paypal: "PayBito Titan",
                        kurvpay: "PayBito Nexus",
                        netbilling: "PayBito Vertex",
                        hostmerchantservices: "PayBito Nova",
                        cardflo: "PayBito Sovereign",
                        nmi: "PayBito Zenith",
                        durangomerchantservices: "PayBito Quantum"
                      };
                      const displayName = map[normalized] ? `${map[normalized]} Application` : `${app.cardProcessorName || "Card"} Application`;

                      let statusColor = "var(--text-color)";
                      if (app.applicationStatus === "Approved") statusColor = "#10b981";
                      if (app.applicationStatus === "Pending" || app.applicationStatus === "Submitted") statusColor = "#f59e0b";
                      if (app.applicationStatus === "Declined") statusColor = "#ef4444";

                      return (
                        <div key={index} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "var(--body-color)", borderRadius: "8px", border: "1px solid var(--body-color-less2)" }}>
                          <span style={{ fontWeight: "600", fontSize: "14px", color: "var(--text-color)" }}>{displayName}</span>
                          <span style={{ fontWeight: "700", fontSize: "12px", padding: "4px 12px", borderRadius: "20px", background: `color-mix(in srgb, ${statusColor}, transparent 90%)`, color: statusColor }}>
                            {app.applicationStatus}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── All complete ── */}
              {allComplete && (
                <div style={sx.allCompleteBanner}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#10b981",
                    }}
                  >
                    All steps completed! Your account is fully set up.
                  </span>
                </div>
              )}
            </div>
            {/* /main-inner */}
            <Footer />
          </div>

          <Loader show={this.state.showLoader} />
        </div>
      </Fragment>
    );
  }
}

/* ──────────────────────────────────────────────────────────────────────────
   Inline SVG icons
────────────────────────────────────────────────────────────────────────── */
const CheckSVG = ({ color }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="14"
    height="14"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const InfoSVG = ({ color }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="14"
    height="14"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="3" />
  </svg>
);
const UserSVG = ({ color }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="14"
    height="14"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const BoltSVG = ({ color }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    width="14"
    height="14"
  >
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

/* ──────────────────────────────────────────────────────────────────────────
   Dynamic style helpers
────────────────────────────────────────────────────────────────────────── */
function stepCircleStyle(done, isActive) {
  const base = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.3s",
    boxSizing: "border-box",
  };
  if (done)
    return {
      ...base,
      background: "#10b981",
      boxShadow: "0 0 0 4px rgba(16,185,129,0.15)",
    };
  if (isActive)
    return {
      ...base,
      background: "transparent",
      border: "2px solid #3b6ef6",
      animation: "gs-pulse-step 2s infinite",
    };
  return { ...base, background: "var(--body-color-less2)", opacity: 0.45 };
}

function stepLabelStyle(done, isActive) {
  const base = {
    fontSize: "11px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    marginTop: "8px",
    letterSpacing: "0.2px",
  };
  if (done) return { ...base, color: "#10b981" };
  if (isActive) return { ...base, color: "#3b6ef6" };
  return { ...base, color: "var(--text-color)", opacity: 0.4, fontWeight: 500 };
}

function connectorStyle(doneBefore) {
  const base = {
    height: "2px",
    flex: 1,
    margin: "0 4px",
    marginBottom: "22px",
    borderRadius: "2px",
  };
  return doneBefore
    ? { ...base, background: "#10b981" }
    : {
      ...base,
      background:
        "repeating-linear-gradient(90deg,#3b6ef6 0,#3b6ef6 6px,transparent 6px,transparent 12px)",
      opacity: 0.4,
    };
}

/* ──────────────────────────────────────────────────────────────────────────
   Static styles
────────────────────────────────────────────────────────────────────────── */
const sx = {
  mainInner: { padding: "36px 40px 0", maxWidth: "900px" },

  arrivalBanner: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "rgba(16,185,129,0.08)",
    border: "1px solid rgba(16,185,129,0.3)",
    borderRadius: "10px",
    padding: "10px 16px",
    marginBottom: "28px",
    animation: "gs-banner-in 0.4s ease both",
  },
  arrivalClose: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    lineHeight: 1,
    opacity: 0.7,
    padding: "2px 4px",
    borderRadius: "4px",
    flexShrink: 0,
    color: "#10b981",
  },

  stepProgress: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
    margin: "0 auto 28px",
    maxWidth: "460px",
  },
  stepNode: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    position: "relative",
    zIndex: 1,
  },

  pageHeader: { marginBottom: "24px", marginTop: "8px" },
  pageTitle: {
    fontSize: "28px",
    fontWeight: 700,
    letterSpacing: "-0.5px",
    lineHeight: 1.2,
    margin: 0,
  },
  pageSubtitle: {
    fontSize: "14px",
    opacity: 0.55,
    marginTop: "8px",
    marginBottom: 0,
  },

  actionButtons: {
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
    marginBottom: "32px",
  },
  btnPrimary: {
    height: "44px",
    padding: "0 22px",
    borderRadius: "8px",
    background: "linear-gradient(135deg, var(--btn-color), #6366f1)",
    border: "none",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 0 20px rgba(59,110,246,0.35)",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  btnOutline: {
    height: "44px",
    padding: "0 22px",
    borderRadius: "8px",
    background: "transparent",
    border: "1.5px solid var(--btn-color)",
    color: "var(--btn-color)",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },

  checklistCard: {
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "32px",
  },
  cardLabel: {
    fontSize: "12px",
    fontWeight: 600,
    opacity: 0.5,
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    marginBottom: "4px",
  },
  cardLabelSub: { fontSize: "12px", opacity: 0.5, marginBottom: "16px" },

  checklistRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "12px 0",
    borderBottom: "1px solid var(--body-color-less2)",
  },
  rowFresh: { animation: "gs-fresh 0.8s ease 0.4s both" },

  checkIconGreen: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: "rgba(16,185,129,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  checkIconBlue: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: "color-mix(in srgb, var(--btn-color), transparent 85%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  checkLabelWrap: { flex: 1, minWidth: 0 },
  checkLabel: { fontSize: "14px", fontWeight: 500 },
  checkStatusGreen: {
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    color: "#10b981",
    whiteSpace: "nowrap",
  },
  checkStatusBlue: {
    fontSize: "10px",
    fontWeight: 600,
    marginTop: "2px",
    letterSpacing: "0.3px",
    color: "var(--btn-color)",
  },

  btnMini: {
    height: "28px",
    padding: "0 14px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    transition: "all 0.15s",
    /* Note: global CSS overrides background/border/color on <button>.
       We rely on that here — it will apply --btn-color automatically. */
  },
  btnMiniLink: {
    height: "28px",
    padding: "0 14px",
    borderRadius: "6px",
    background: "color-mix(in srgb, var(--btn-color), transparent 85%)",
    border: "1px solid var(--btn-color)",
    color: "var(--btn-color)",
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    lineHeight: "28px",
    transition: "all 0.15s",
  },
  sentLabel: {
    fontSize: "12px",
    color: "#10b981",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  allCompleteBanner: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "rgba(16,185,129,0.08)",
    border: "1px solid rgba(16,185,129,0.2)",
    borderRadius: "12px",
    padding: "14px 20px",
    marginBottom: "32px",
  },
  promoCard: {
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "32px",
    position: "relative",
    overflow: "hidden",
  },
  promoContent: {
    display: "flex",
    gap: "20px",
    alignItems: "flex-start",
  },
  promoIcon: {
    fontSize: "32px",
    background: "rgba(59,110,246,0.1)",
    width: "60px",
    height: "60px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  promoTitle: {
    fontSize: "18px",
    fontWeight: 700,
    marginBottom: "8px",
    margin: 0,
  },
  promoText: {
    fontSize: "14px",
    opacity: 0.7,
    lineHeight: 1.5,
    marginBottom: "16px",
  },
  promoList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    fontSize: "13px",
    fontWeight: 500,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    color: "#10b981",
  },
  promoAction: {
    display: "flex",
    alignItems: "center",
  },
  btnSuccess: {
    height: "40px",
    padding: "0 24px",
    borderRadius: "8px",
    background: "#10b981",
    border: "none",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
};

export default GetStartedNew;
